import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { CheckOutlined, CopyOutlined, LeftOutlined } from '@ant-design/icons';
import { Alert, Button, Collapse, Drawer, Empty, Spin, message } from 'antd';
import ProjectApplicationDefinitionEditVisualStore, {
    FieldSettings
} from '../stores/ProjectApplicationDefinitionEditVisualStore';
import { ApplicationFieldQueryDefinition, ApplicationQueryVariantDefinition, InputMeta } from '../types';
import ApplicationFieldSettingsModal from './ApplicationFieldSettingsModal';
import './FieldSettingsDrawer.less';

const { Panel } = Collapse;
const GENERAL_INPUTS_SECTION_KEY = 'general-inputs';
const DRAWER_MASK_STYLE: React.CSSProperties = { backgroundColor: 'transparent' };

type FieldSettingsSection = {
    key: string;
    title: string;
    fields: InputMeta[];
};

type Props = {
    store: ProjectApplicationDefinitionEditVisualStore;
    isVisible: boolean;
    isDisabled: boolean;
    onClose: () => void;
    onFieldSettingsChange: () => void;
};

const FieldSettingsDrawer: React.FC<Props> = ({ store, isVisible, isDisabled, onClose, onFieldSettingsChange }) => {
    const [selectedField, setSelectedField] = React.useState<InputMeta | null>(null);
    const [selectedQueryInputId, setSelectedQueryInputId] = React.useState<string | null>(null);
    const currentApplicationId = store.currentAppDef?.id;
    const selectedQueryDefinition = selectedQueryInputId
        ? store.getApplicationQueryDefinition(selectedQueryInputId)
        : undefined;
    const selectedQueryField = selectedQueryInputId
        ? store.fieldSettingsInputs.find(field => field.id === selectedQueryInputId)
        : undefined;

    React.useEffect(() => {
        if (!isVisible) {
            setSelectedField(null);
            setSelectedQueryInputId(null);
        }
    }, [isVisible]);

    React.useEffect(() => {
        setSelectedQueryInputId(null);
    }, [currentApplicationId]);

    const handleSubmit = (values: FieldSettings) => {
        if (!selectedField || isDisabled) {
            return;
        }

        store.setFieldSettings(selectedField.id, values);
        onFieldSettingsChange();
        setSelectedField(null);
    };

    const renderFieldDescription = (field: InputMeta) => {
        if (!field.description) {
            return null;
        }

        return <div className="group-subtitle field-settings-drawer__field-description">{field.description}</div>;
    };

    const renderFieldRow = (field: InputMeta) => {
        const settings = store.getFieldSettings(field);
        const queryDefinition = store.getApplicationQueryDefinition(field.id);

        return (
            <div
                key={field.id}
                data-id={`field-settings-row-${field.id}`}
                onClick={() => {
                    if (!isDisabled) {
                        setSelectedField(field);
                    }
                }}
                className={`field-settings-drawer__row${isDisabled ? ' field-settings-drawer__row--disabled' : ''}`}
            >
                <div className="field-settings-drawer__field-content">
                    <div className="group-title field-settings-drawer__field-name">{field.name}</div>
                    {renderFieldDescription(field)}
                    {queryDefinition ? (
                        <Button
                            className="field-settings-drawer__view-query"
                            type="link"
                            size="small"
                            onClick={event => {
                                event.stopPropagation();
                                setSelectedQueryInputId(field.id);
                            }}
                        >
                            View SQL
                        </Button>
                    ) : null}
                </div>

                <div
                    className={`field-settings-drawer__status-cell${
                        settings.isVisible ? ' field-settings-drawer__status-cell--active' : ''
                    }`}
                    aria-label={`${field.name} visible`}
                >
                    {settings.isVisible ? (
                        <span
                            aria-hidden="true"
                            className="field-settings-drawer__status-icon field-settings-drawer__status-icon--visible"
                        >
                            <CheckOutlined />
                        </span>
                    ) : (
                        <span aria-hidden="true" className="field-settings-drawer__status-empty">
                            -
                        </span>
                    )}
                </div>

                <div
                    className={`field-settings-drawer__status-cell${
                        settings.isRequired ? ' field-settings-drawer__status-cell--active' : ''
                    }`}
                    aria-label={`${field.name} required`}
                >
                    {settings.isRequired ? (
                        <span
                            aria-hidden="true"
                            className="field-settings-drawer__status-icon field-settings-drawer__status-icon--required"
                        >
                            <CheckOutlined />
                        </span>
                    ) : (
                        <span aria-hidden="true" className="field-settings-drawer__status-empty">
                            -
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const renderSectionStatusHeader = () => (
        <div className="field-settings-drawer__header">
            <div />
            <div className="field-settings-drawer__header-label">Visible</div>
            <div className="field-settings-drawer__header-label">Required</div>
        </div>
    );

    const fieldSettingsInputs = store.fieldSettingsInputs;
    const collapseKey = isVisible ? 'field-settings-collapse-open' : 'field-settings-collapse-closed';
    const sections = React.useMemo<FieldSettingsSection[]>(() => {
        const sectionMap = new Map<string, FieldSettingsSection>();

        fieldSettingsInputs.forEach(input => {
            const key = input.section || GENERAL_INPUTS_SECTION_KEY;
            const section = sectionMap.get(key);

            if (section) {
                section.fields.push(input);
                return;
            }

            sectionMap.set(key, {
                key,
                title: input.section || 'Inputs',
                fields: [input]
            });
        });

        return Array.from(sectionMap.values());
    }, [fieldSettingsInputs]);

    const copySql = async (sql: string) => {
        try {
            if (!navigator.clipboard?.writeText) {
                throw new Error('Clipboard is not supported.');
            }

            await navigator.clipboard.writeText(sql);
            message.success('SQL copied to clipboard.');
        } catch {
            message.error('Unable to copy SQL to clipboard.');
        }
    };

    const renderSql = (variant: ApplicationQueryVariantDefinition, sql: string, label: string) => (
        <div className="field-settings-drawer__sql-block">
            <div className="field-settings-drawer__sql-header">
                <span className="field-settings-drawer__detail-label">{label}</span>
                <Button
                    size="small"
                    icon={<CopyOutlined />}
                    aria-label={`Copy ${label.toLowerCase()} for ${variant.name}`}
                    onClick={() => void copySql(sql)}
                >
                    Copy
                </Button>
            </div>
            <pre>{sql}</pre>
        </div>
    );

    const renderVariant = (variant: ApplicationQueryVariantDefinition) => (
        <section className="field-settings-drawer__variant" key={variant.id}>
            <h3>{variant.name}</h3>
            <dl className="field-settings-drawer__details">
                <dt>Variant ID</dt>
                <dd>{variant.id}</dd>
                <dt>Query</dt>
                <dd>{variant.queryName}</dd>
                <dt>Query ID</dt>
                <dd>{variant.queryId}</dd>
                <dt>Template</dt>
                <dd>{variant.isTemplate ? 'Yes' : 'No'}</dd>
                <dt>Condition</dt>
                <dd>
                    {variant.condition
                        ? `${variant.condition.settingId} = ${variant.condition.expectedValue}`
                        : 'Always available'}
                </dd>
            </dl>

            <h4>Parameters</h4>
            {variant.parameters.length ? (
                <div className="field-settings-drawer__parameters">
                    {variant.parameters.map(parameter => (
                        <dl className="field-settings-drawer__parameter" key={parameter.name}>
                            <dt>Name</dt>
                            <dd>{parameter.name}</dd>
                            <dt>Type</dt>
                            <dd>{parameter.valueType}</dd>
                            <dt>Binding</dt>
                            <dd>{parameter.bindingKind}</dd>
                            {parameter.source ? (
                                <>
                                    <dt>Source</dt>
                                    <dd>{parameter.source}</dd>
                                </>
                            ) : null}
                            {parameter.value != null ? (
                                <>
                                    <dt>Value</dt>
                                    <dd>{parameter.value}</dd>
                                </>
                            ) : null}
                            <dt>Description</dt>
                            <dd>{parameter.description}</dd>
                        </dl>
                    ))}
                </div>
            ) : (
                <div className="field-settings-drawer__empty-detail">No parameters</div>
            )}

            {renderSql(variant, variant.sql, 'SQL')}
        </section>
    );

    const renderQueryDefinition = (definition: ApplicationFieldQueryDefinition) => (
        <div className="field-settings-drawer__query-view">
            <dl className="field-settings-drawer__details field-settings-drawer__source-details">
                <dt>Field</dt>
                <dd>{selectedQueryField?.name ?? definition.inputId}</dd>
                <dt>Source</dt>
                <dd>{definition.sourceName}</dd>
                <dt>Source ID</dt>
                <dd>{definition.sourceId}</dd>
                <dt>Input ID</dt>
                <dd>{definition.inputId}</dd>
            </dl>
            <p className="field-settings-drawer__query-note">
                These are application-configured variants. The session-effective variant is not shown here.
            </p>
            {definition.variants.map(renderVariant)}
        </div>
    );

    const renderList = () => (
        <>
            {store.applicationQueryDefinitionsError ? (
                <Alert
                    className="field-settings-drawer__query-error"
                    type="error"
                    showIcon
                    message={store.applicationQueryDefinitionsError}
                    action={
                        <Button size="small" onClick={store.retryApplicationQueryDefinitions}>
                            Retry
                        </Button>
                    }
                />
            ) : null}
            <Spin spinning={store.isApplicationQueryDefinitionsLoading} tip="Loading SQL query definitions...">
                {fieldSettingsInputs.length ? (
                    <Collapse
                        key={collapseKey}
                        className="alpha-collapse-container field-settings-collapse"
                        bordered={false}
                        expandIconPosition="left"
                    >
                        {sections.map(section => (
                            <Panel
                                className="alpha-collapse-panel"
                                key={section.key}
                                header={section.title}
                                forceRender
                            >
                                {renderSectionStatusHeader()}
                                {section.fields.map(renderFieldRow)}
                            </Panel>
                        ))}
                    </Collapse>
                ) : (
                    <Empty description="No fields available" />
                )}
            </Spin>
        </>
    );

    const drawerFooter = (
        <div className="field-settings-drawer__footer">
            {selectedQueryDefinition ? (
                <Button icon={<LeftOutlined />} onClick={() => setSelectedQueryInputId(null)}>
                    Back
                </Button>
            ) : (
                <span />
            )}
            <Button onClick={onClose}>Close</Button>
        </div>
    );

    return (
        <>
            <Drawer
                className="field-settings-drawer"
                data-id="field-settings-drawer"
                visible={isVisible}
                placement="right"
                title={
                    selectedQueryDefinition
                        ? `SQL Queries - ${selectedQueryField?.name ?? selectedQueryDefinition.inputId}`
                        : 'Field Settings'
                }
                width={520}
                mask
                maskClosable
                maskStyle={DRAWER_MASK_STYLE}
                onClose={onClose}
                footer={drawerFooter}
            >
                {selectedQueryDefinition ? renderQueryDefinition(selectedQueryDefinition) : renderList()}
            </Drawer>

            <ApplicationFieldSettingsModal
                field={selectedField}
                isVisible={!!selectedField}
                isDisabled={isDisabled}
                initialValues={
                    selectedField ? store.getFieldSettings(selectedField) : { isVisible: true, isRequired: false }
                }
                onCancel={() => setSelectedField(null)}
                onSubmit={handleSubmit}
            />
        </>
    );
};

export default observer(FieldSettingsDrawer);
